import PageLoader from '@/common/service/flow/page-loader';
import { buildAccountBaseService } from '@/common/service/base-paid';
import { loadSelectByPersist } from '@/common/service/plan-data-mapper';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import store from '@/store/index';
import { freeTrialUpgrade, proUpgrade } from '@/apis';
import _, { get } from 'lodash';
import { pollingEventStatus } from '@/common/polling';
import {
  gotoSuccessPage,
  gotoWiseSuccessPage,
  upgradeSuccessPage,
  wiseUpgradeSuccessPage,
} from '@/common/service/common';
import { alertWithPendingCoupon } from '@/common/service/validate/coupon';
import { VIEWS } from '@/views/bridge';
import { isEmptyVal } from '@/utils';
import { addToCartByCategoryAndBillingCycle, getCurrentPlanCycleDepFirstPlanForInit } from '@/common/service/flow/utils';
import { BILLING_CYCLE, BillingCycleEnum } from '@/common/constants';
import { getBasePlanInfo } from '@/common/product';
import { updatePlansCheckout, UpdateTypes , getPaymentInfo, addParamsIfApm } from '@/common/service/checkout-utils';
import Flow from '@/flows/flow';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { getAssignLicenseResult } from '@/common/service/validate/assign-license';
import BillingMessageBox from '@/components/common/message-box';
import { translation } from '@/plugins/i18n';
import { formJump } from '@/common/utils';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { getBizMeetingPlan } from '@/plans/ZOOM_MEETING/common/utils';
import { getBundlePbxPlan } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { getBiz2BizPlusUpdateForm } from '@/flows/EDIT/index';

const ENUM_UPGRADE_TYPE = {
  // FREE TRIAL
  ONLINE_FREE_TRIAL_UPGRADE: 2 // backend enum value, could be more in future
}

const loadDefaultSelectByQueryForPro2Biz = (boughtPlans = []) => {
  return (planList = []) => {
    const { targetCategory = [], planType } = pageConfig?.editConfig || {};

    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    const boughtBasePlan = boughtPlans?.find((p) => p.type === planType);

    if (!hasSelected) {
      const { billing_cycle } = boughtBasePlan || {};

      return planList.map((plan) => {
        const isMatchTargetPlan = plan.category === targetCategory
          && plan.billing_cycle === billing_cycle;

        return isMatchTargetPlan ? {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: Math.max(plan.min, boughtBasePlan?.cartInfo.selected_number || 1),
          },
        } : plan;
      });
    }

    return planList;
  };
};

const loadDefaultSelectByQueryForUpgrade = (planList = []) => {
  const { plan } = pageConfig?.editConfig || {};

  const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
  if(hasSelected) {
    return planList;
  }

  addToCartByCategoryAndBillingCycle(planList, getPlanTypeByCategory(plan), plan, BILLING_CYCLE.ANNUAL);

  return planList;
};

const initShoppingCartForPro2Biz = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForPro2Biz(boughtPlans));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const initShoppingCartForUpgrade = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForUpgrade);
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const checkOutForPro2BizSubmit = async () => {
  const { apmInfo } = store.getters;
  let params = get(store.getters?.selectedPlanListPayLoad, '[0]', {});
  params = { ...params, indiaCustomerNonINR: false };
  addParamsIfApm(params);
  await proUpgrade(params);
  const eventResult = await pollingEventStatus({rps: true});
  if (apmInfo.isApm) {
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName
    }
    apmInfo.apmPaymentMethodName === WISE ? wiseUpgradeSuccessPage({}) : gotoSuccessPage(successParams);
    return {
      eventResult,
    };
  }

  gotoSuccessPage();

  return {
    eventResult,
  };
};

const checkOutForUpgrade = async () => {
  const getters = store.getters;
  const { planType } = pageConfig.editConfig;
  const currentSelectedPlan = getters.getSelectedPlans.find(item => item.type === planType);
  const boughtBasePlan = getBasePlanInfo(getters.boughtPlans);
  const oldPlanCode = get(boughtBasePlan, 'plans.0.plan_code', '');
  // debugger;
  const actionType = UpdateTypes.ZONE_BASIC_PLUS_UPGRADE_TO_MEETING;
  const planList = [{
    quantity: currentSelectedPlan.cartInfo.selected_number,
    planCode: currentSelectedPlan.plan_code,
    oldPlanCode,
  }];
  return updatePlansCheckout(planList, actionType);
};

const loadDefaultSelectByQueryForTrial2Paid = (trialPlans = [], boughtPlans = []) => {
  return (planList = []) => {
    const { planType, category } = pageConfig?.editConfig || {};

    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    const basePlan = trialPlans?.find((p) => p.type === planType);

    if (!hasSelected) {

      const { billing_cycle, cartInfo = {} } = basePlan || {};
      let period = billing_cycle;

      const currentPlanCycleDepFirstPlan = getCurrentPlanCycleDepFirstPlanForInit(trialPlans, boughtPlans, planType);

      // compare depend base plan billing cycle with url query period(billing cycle) parameter
      if (currentPlanCycleDepFirstPlan && BillingCycleEnum[period] > currentPlanCycleDepFirstPlan?.cycleValue) {
        period = currentPlanCycleDepFirstPlan?.billing_cycle;
      }
      addToCartByCategoryAndBillingCycle(planList, planType, category, period, cartInfo.selected_number || 1);
    }

    return planList;
  };
};

const initShoppingCartTrial2Paid = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  const currentEditPlan = _.find(freeTrialPlans, (plan) => plan.plan_code === pageConfig.oldPlanCode) || {};
  const assignResult = await getAssignLicenseResult(currentEditPlan);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForTrial2Paid(freeTrialPlans, boughtPlans));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const initShoppingCartForBiz2Bundle = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  // biz plan
  const bizPlan = getBizMeetingPlan(boughtPlans);
  const assignResult = await getAssignLicenseResult(bizPlan);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForBiz2Bundle(boughtPlans, assignResult));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const loadDefaultSelectByQueryForBiz2Bundle = (boughtPlans = [], assignResult = {}) => {
  return (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const planInfo = getBundlePbxPlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  };
};

const checkOutForBiz2BundleSubmit = async () => {
  const getters = store.getters;
  const updateForm = getBiz2BizPlusUpdateForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  return updatePlansCheckout(updateForm.planList, updateForm.actionType, couponData, updateForm.extensionUpdateData);
};

const checkOutForTrial2Paid = async () => {
  let params = get(store.getters?.selectedPlanListPayLoad, '[0]', {});

  const getters = store.getters;

  const { couponCode = '' } = getters.couponState;
  const { campaign_code: campaignCode = '', trial_code: trialCode = '' } = pageConfig || {};

  params = {
    upgradeList: [{
      oldPlanCode: pageConfig.oldPlanCode,
      planCode: params.planCode,
      quantity: params.quantity,
    }],
    addList: [],
    coupon: { fromCampaignLink: getters.fromCampaignLink, code: couponCode },
    // FREE TRIAL - online free trial upgrade need 2 new params
    ...(campaignCode ? { campaignCode }: {}),
    ...(trialCode ? { trialCode }: {}),
    ...getPaymentInfo(getters),
  };

  if(!!campaignCode && !!trialCode) {
    params.bizType = ENUM_UPGRADE_TYPE.ONLINE_FREE_TRIAL_UPGRADE; // online free trial upgrade, could be more enum type in the future
  }

  params = { ...params, indiaCustomerNonINR: false };
  addParamsIfApm(params);

  await freeTrialUpgrade(params);
  const eventResult = await pollingEventStatus({rps: true});
  const { apmInfo } = store.getters;
  if (apmInfo.isApm) {
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName
    }
    apmInfo.apmPaymentMethodName === WISE ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue }) : gotoSuccessPage(successParams);
    return {
      eventResult,
    }
  }

  upgradeSuccessPage(undefined, undefined, 2000, getters?.currentEditPlan?.type);
  return {
    eventResult,
  };
};

const resolveView = () => VIEWS.COMPOUND;

const preCheckForUpgrade = async () => {
  const { hasPendingCoupon, pendingCouponEffectiveDate } = store.getters.basicConfig;

  if (hasPendingCoupon && !isEmptyVal(pendingCouponEffectiveDate)) {
    return alertWithPendingCoupon();
  }

  return Promise.resolve();
};

const checkAssignLicenseForFree2Paid = async () => {
  const { assignInfo, getSelectedPlans, freeTrialPlans } = store.getters;
  const currentEditPlan = _.find(freeTrialPlans, (plan) => plan.plan_code === pageConfig.oldPlanCode) || {};
  const currentSelectedPlan = getSelectedPlans.find(item => item.type === currentEditPlan.type);
  const currentAssignedLicense = _.get(assignInfo, [pageConfig.oldPlanCode, 'assignLicenseQuantity'], 0);
  if(
    currentSelectedPlan && currentAssignedLicense &&
    currentSelectedPlan.cartInfo.selected_number < currentAssignedLicense
  ) {
    const args = [currentAssignedLicense, currentAssignedLicense - currentSelectedPlan.cartInfo.selected_number]
    if(window.newBillingConfig.isPaid) {
      return BillingMessageBox.confirm(
        translation('billing.planform.zoom_meeting.less_than_assigned_number_tips', args),
        translation('billing.cancel_title_sorry_to_see_you_go'),
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: translation('billing.success.go_to_user_management'),
          cancelButtonText: translation('billing.common.cancel'),
        },
      ).then(() => {
        formJump('/account/user', 'GET');
        return Promise.reject();
      }).catch(() => Promise.reject());
    }
    return BillingMessageBox.alert(
      translation('billing.planform.zoom_meeting.less_than_assigned_number_tips_for_expire', args),
      translation('billing.cancel_title_sorry_to_see_you_go'),
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: translation('billing.got_it'),
      },
    ).then(() => {
      return Promise.reject();
    });
  }
  return Promise.resolve();
}

export default {
  [flowTypes.upgrade]: class extends Flow{
    view(){ return resolveView() }
    init(){ return initShoppingCartForUpgrade(...arguments) }
    submit(){ return checkOutForUpgrade() }
  },
  [flowTypes.pro2biz]: class extends Flow{
    view(){ return resolveView() }
    init(){ return initShoppingCartForPro2Biz(...arguments) }
    beforeSubmit(){ return preCheckForUpgrade() }
    submit() { return checkOutForPro2BizSubmit() }
  },
  [flowTypes.trial2paid]: class extends Flow {
    view(){ return pageConfig.hasBillingZuoraId ? VIEWS.COMPOUND : VIEWS.CONFIG }
    init(){ return initShoppingCartTrial2Paid(...arguments) }
    beforeSubmit(){ return checkAssignLicenseForFree2Paid() }
    submit(){ return checkOutForTrial2Paid() }
  },
  [flowTypes.biz2bundle]: class extends Flow{
    view(){ return resolveView() }
    init(){ return initShoppingCartForBiz2Bundle(...arguments) }
    beforeSubmit(){ return preCheckForUpgrade() }
    submit() { return checkOutForBiz2BundleSubmit() }
  },
};
